import Api from "../services/api";
import { changeTimeZoneForDstCalculator, ITimeZoneInfo } from "./date";
import { dispatcher, DispatcherEvents } from "./dispatcher";
import { CancelablePromise as Promise } from "./cancelablePromise";
import { UserService } from "./users";
import { SecurityService } from "./security";
import { DriveStore } from "../store/reducers";
import { TimeZoneInfoActions } from "../store/timeZoneInfo/actions";
import { DataActions } from "../store/data/actions";
import { extend } from "./vanilla";
import * as logger from "./logger";
import { HOURS } from "../constants";

export const UserChangePasswordRequired = "Expired password. User must change password";

// Servers send baseUtcOffsetDelta on every rule ("00:00:00" included) since MYG-153516; it is only missing from rules
// saved before that, or when the HOS.DISABLE_BASE_UTC_OFFSET_DELTA.KILLSWITCH custom code is on.
const predatesBaseUtcOffsetDelta = (timeZoneInfo: DeepReadonly<ITimeZoneInfo>): boolean => {
    const rules = timeZoneInfo.adjustmentRules;
    return Array.isArray(rules) && rules.length > 0 && rules.every(rule => !rule.baseUtcOffsetDelta);
};

/** How often old saved rules are fetched again while the server still leaves the field out; matches @userRefresh. */
const REFETCH_INTERVAL = 6 * HOURS;

/** When each zone's old saved rules were last fetched again, so a server that never sends the field is asked once per interval. */
let refetchedAt = new Map<string, number>();

/** @internal Exposed for testing only. Forgets when zones were fetched again. */
export function resetRefetchedTimeZoneIds() {
    refetchedAt = new Map<string, number>();
}

export class UserUpdater {

    constructor(
        private readonly api: Api,
        private readonly userService: UserService,
        private readonly securityService: SecurityService,
        private readonly store: DriveStore
    ) {}

    updateUser(
        username: string,
        callback?: (user?: INamedEntity, errorMessage?: string) => void,
        forceGetPermissions?: boolean,
        forceGetUserData?: boolean
    ) {
        const securityGroups = this.store.getState().data.groupSecurityId;
        const needsGroupFetch = forceGetPermissions || securityGroups.length <= 0;

        const userObject = this.userService.getUserByName(username);
        const acl: string[] | undefined = userObject
            ? extend(true, [], this.store.getState().data.acl[userObject.id] || [])
            : undefined;
        const needsUserFetch = forceGetPermissions || forceGetUserData || !userObject || !userObject.securityGroups || !acl;

        let getUserData: Promise<IUser>;
        let getUserPermissions: Promise<ISecurityGroup[]>;

        if (needsGroupFetch && needsUserFetch) {
            // Both calls are needed — batch them into a single multiCall to save one HTTP round-trip
            const multiCallResult = new Promise<[IUser, ISecurityGroup[]]>((resolve, reject) => {
                this.api.multiCall([
                    ["Get", { typeName: "Group", search: { id: "GroupSecurityId" }, propertySelector: { fields: ["id", "name", "children", "securityFilters"], isIncluded: true } }],
                    ["Get", { typeName: "User", search: { name: username } }]
                ], ([groupResults, userData]: [ISecurityGroup[], IUser[]]) => {
                    this.store.dispatch(DataActions.setGroupSecurityId(groupResults));
                    resolve([userData[0], extend(true, [], groupResults)]);
                }, reject);
            });
            getUserData = multiCallResult.then(([user]) => user);
            getUserPermissions = multiCallResult.then(([, permissions]) => permissions);
        } else {
            getUserPermissions = new Promise<ISecurityGroup[]>((resolve, reject) => {
                if (needsGroupFetch) {
                    this.api.call("Get", {
                        typeName: "Group",
                        search: {
                            id: "GroupSecurityId"
                        },
                        propertySelector: {
                            fields: ["id", "name", "children", "securityFilters"],
                            isIncluded: true
                        }
                    }, (securityGroupResults: ISecurityGroup[]) => {
                        this.store.dispatch(DataActions.setGroupSecurityId(securityGroupResults));
                        resolve(extend(true, [], securityGroupResults));
                    }, reject);
                } else {
                    resolve(extend(true, [], securityGroups) as ISecurityGroup[]);
                }
            });

            getUserData = new Promise<IUser>((resolve, reject) => {
                if (needsUserFetch) {
                    this.api.call("Get", {
                        typeName: "User",
                        search: { name: username }
                    }, data => {
                        resolve(data[0]);
                    }, reject);
                } else {
                    resolve(userObject as IUser);
                }
            });
        }

        return Promise.all([getUserData, getUserPermissions]).then(([userData, permissions]) => {
            let updateUserAndCallback = (user, clearances?) => {
                this.userService.addUser(user);

                // Reinit the dstCalculator
                changeTimeZoneForDstCalculator(user.timeZoneId, this.userService.getUsersTimezoneInfo());

                // If we have security clearances
                // IMPORTANT: This must follow add the user
                if (clearances) {
                    let newAcl = this.securityService.calculatePermissions(clearances, user.securityGroups);
                    this.store.dispatch(DataActions.setAcl({
                        [user.id]: newAcl
                    }));
                }

                // Trigger user change
                dispatcher.trigger(DispatcherEvents.UserUpdate, user);

                callback && callback(user);
            };

            if (!userData) {
                // User was deleted
                callback && callback();
                return undefined;
            }
            const existingUser = this.userService.getUserByName(username);
            const hasTimezoneChanged = !existingUser || userData.timeZoneId !== existingUser.timeZoneId;

            // Only forced refreshes fetch old saved rules again, so cached paths such as storage-full recovery stay offline
            const refreshSaved = Boolean(forceGetPermissions || forceGetUserData);

            return this.updateUserTimezoneInfo(userData.id, userData.timeZoneId || "", hasTimezoneChanged, refreshSaved)
                .then(() => updateUserAndCallback(userData, permissions));
        });
    }
    private updateUserTimezoneInfo (userId: string, timeZoneId: string, force = false, refreshSaved = false) {
        return new Promise<ITimeZoneInfo | undefined>((resolve, reject) => {
            let userTimezoneInfo = this.store.getState().timeZoneInfo[userId];

            if (force || !userTimezoneInfo || timeZoneId !== userTimezoneInfo.timeZoneId) {
                this.api.call("GetDaylightSavingRules", {
                    timeZoneId
                }, (timeZoneInfo: ITimeZoneInfo) => {
                    this.saveForCoDrivers(userId, timeZoneId, timeZoneInfo);
                    resolve(timeZoneInfo);
                }, reject);
            } else if (refreshSaved && predatesBaseUtcOffsetDelta(userTimezoneInfo)) {
                // Saved rules without the standard offset shift resolve zones like British Columbia an hour off from
                // 2026-11-01, so fetch them again until a call succeeds, then at most once per interval while the
                // server still leaves the field out. Nothing is saved unless the server answers, so an overlapping
                // refresh can't put the old rules back.
                const lastRefetch = refetchedAt.get(timeZoneId);
                if (lastRefetch !== undefined && Date.now() - lastRefetch < REFETCH_INTERVAL) {
                    resolve(undefined);
                    return;
                }
                this.api.call("GetDaylightSavingRules", {
                    timeZoneId
                }, (timeZoneInfo: ITimeZoneInfo) => {
                    refetchedAt.set(timeZoneId, Date.now());
                    this.saveForCoDrivers(userId, timeZoneId, timeZoneInfo);
                    resolve(timeZoneInfo);
                }, error => {
                    logger.error(logger.Source.USER, `Couldn't refresh the daylight saving rules for ${ timeZoneId }`, this.api.getUser(), error);
                    resolve(undefined);
                });
            } else {
                resolve(extend(true, {}, userTimezoneInfo) as ITimeZoneInfo);
            }
        }).then(timeZoneInfo => {
            timeZoneInfo && this.store.dispatch(TimeZoneInfoActions.setTimeZoneInfo({ userId: userId, timeZoneInfo: timeZoneInfo }));
        });
    }

    // The calculator keeps one rule set per zone and the last active user wins, so co-drivers in the same zone need
    // the rules the server just sent too
    private saveForCoDrivers (userId: string, timeZoneId: string, timeZoneInfo: ITimeZoneInfo) {
        this.userService.getUsers(true)
            .filter(user => user.id !== userId && user.timeZoneId === timeZoneId)
            .forEach(user => this.store.dispatch(TimeZoneInfoActions.setTimeZoneInfo({ userId: user.id, timeZoneInfo })));
    }
}
